import React, { useState, useEffect, useRef } from "react";
import "./InputDropdown.scss";
import { useTranslation } from "react-i18next";
import { FaChevronDown } from "react-icons/fa";
import InputCheckbox from "../InputCheckbox/InputCheckbox";
import { IoClose } from "react-icons/io5";

type Option = {
    value: string;
    label: string;
};

type InputDropdownProps = {
    label?: string;
    labelVisible?: boolean;
    options: Option[];
    onChange?: (value: Option | null) => void;
    placeholder?: string;
    value: Option;
    disabled?: boolean;
    withCheckbox?: boolean;
    className?: string;
};

const InputDropdown: React.FC<InputDropdownProps> = ({
    label,
    labelVisible = true,
    options,
    onChange,
    placeholder: placeholderProp,
    value,
    disabled = false,
    withCheckbox = false,
    className = "",
}) => {
    const { t } = useTranslation();
    const placeholder = placeholderProp ?? (t("ui.select.selectOption") ?? "");
    const [isOpen, setIsOpen] = useState(false);
    const [checkedValues, setCheckedValues] = useState<string[]>([]);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleReset = (event: React.MouseEvent) => {
        event.stopPropagation();
        setCheckedValues([]);
        if (onChange) onChange(null);
    };

    const toggleDropdown = () => {
        if (!disabled) {
            setIsOpen((prevState) => !prevState);
        }
    };

    const handleRemoveCheckedValue = (value: string) => {
        setCheckedValues(checkedValues.filter((v) => v !== value));
    };

    return (
        <div
            className={`input-dropdown ${
                disabled ? "input-dropdown--disabled" : ""
            } ${isOpen ? "input-dropdown--open" : ""} ${className}`}
            ref={dropdownRef}
        >
            {labelVisible && label && (
                <label className="input-dropdown__label">{label}</label>
            )}
            <div
                className={`input-dropdown__control ${
                    isOpen ? "input-dropdown__control--open" : ""
                }`}
                onClick={toggleDropdown}
            >
                <div className="input-dropdown__value-container">
                    {withCheckbox && checkedValues.length > 0 ? (
                        checkedValues.map((value) => {
                            const option = options.find(
                                (option) => option.value === value
                            );
                            return (
                                option && (
                                    <span
                                        key={option.value}
                                        className="input-dropdown__badge"
                                    >
                                        {option.label}
                                        {IoClose({
                                            className:
                                                "input-dropdown__badge__close",
                                            onClick: () =>
                                                handleRemoveCheckedValue(value),
                                        })}
                                    </span>
                                )
                            );
                        })
                    ) : (
                        <span
                            className={`input-dropdown__placeholder ${
                                !value ? "input-dropdown__placeholder" : ""
                            }`}
                        >
                            {value?.label || placeholder}
                        </span>
                    )}
                </div>

                <span className="input-dropdown__arrow">
                    {FaChevronDown({})}
                </span>
            </div>
            {isOpen && (
                <ul className="input-dropdown__menu">
                    {options.map((option) => (
                        <li
                            key={option.value}
                            className="input-dropdown__option"
                            onClick={() => {
                                if (withCheckbox) {
                                    const newCheckedValues =
                                        checkedValues.includes(option.value)
                                            ? checkedValues.filter(
                                                  (v) => v !== option.value
                                              )
                                            : [...checkedValues, option.value];
                                    setCheckedValues(newCheckedValues);
                                } else {
                                    if (onChange) onChange(option);
                                    setIsOpen(false);
                                }
                            }}
                        >
                            {withCheckbox ? (
                                <InputCheckbox
                                    id={option.value}
                                    label={option.label}
                                    checked={checkedValues.includes(
                                        option.value
                                    )}
                                    onChange={() => {
                                        const newCheckedValues =
                                            checkedValues.includes(option.value)
                                                ? checkedValues.filter(
                                                      (v) => v !== option.value
                                                  )
                                                : [
                                                      ...checkedValues,
                                                      option.value,
                                                  ];
                                        setCheckedValues(newCheckedValues);
                                    }}
                                />
                            ) : (
                                option.label
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default InputDropdown;
